import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useRefresh } from '../../context/RefreshContext';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api/apiClient';

// ── Business Unit Context ─────────────────────────────────────────────────────
const BuContext = createContext({ selectedBu: null, setSelectedBu: () => {} });

export function BuProvider({ children }) {
  const [selectedBu, setSelectedBu] = useState(null);
  return (
    <BuContext.Provider value={{ selectedBu, setSelectedBu }}>
      {children}
    </BuContext.Provider>
  );
}

export function useBu() {
  return useContext(BuContext);
}

// ── Header Component ──────────────────────────────────────────────────────────
export default function Header({ title }) {
  const { triggerRefresh, refreshing } = useRefresh();
  const { user, myAccounts, switchAccount } = useAuth();
  const { selectedBu, setSelectedBu } = useBu();
  const [units, setUnits] = useState([]);
  const [switching, setSwitching] = useState(false);
  const canRefresh = user?.role === 'admin' || user?.role === 'manager';
  const [syncing, setSyncing] = useState(false);
  const [syncDone, setSyncDone] = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    setSyncDone(false);
    try {
      await apiClient.post('/sync/revenue-balance');
      setSyncDone(true);
      setTimeout(() => setSyncDone(false), 3000);
      triggerRefresh();
    } catch {
      // ignore
    } finally {
      setSyncing(false);
    }
  };

  const loadUnits = useCallback(async () => {
    try {
      const res = await apiClient.get('/business-units');
      setUnits(res.data.units || []);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (user) loadUnits();
  }, [user, loadUnits]);

  const handleRefresh = async () => {
    if (canRefresh) {
      try { await apiClient.delete('/cache'); } catch {}
    }
    triggerRefresh();
  };

  const handleBusinessSwitch = async (e) => {
    const accountId = parseInt(e.target.value);
    if (!accountId || accountId === myAccounts?.currentAccountId) return;
    setSwitching(true);
    try {
      await switchAccount(accountId);
    } catch {
      setSwitching(false);
    }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const showBusinessSwitcher = isAdmin || myAccounts?.accounts?.length > 0;
  const hiddenBUs = new Set(['Operations', 'Memberships', 'Gold Plan']);
  const activeUnits = (() => {
    const filtered = units.filter(u => u.active && !hiddenBUs.has(u.name));
    // Deduplicate by name (Default Account has Walker + Francis with same BU names)
    const seen = new Set();
    return filtered.filter(u => {
      if (seen.has(u.name)) return false;
      seen.add(u.name);
      return true;
    });
  })();

  return (
    <header style={styles.header}>
      <h1 style={styles.title}>{title}</h1>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', flexWrap: 'wrap' }}>

        {/* Business (Account) Switcher */}
        {showBusinessSwitcher && (
          <div style={styles.switcherWrap}>
            <span style={styles.switcherLabel}>🏢</span>
            {myAccounts?.accounts?.length > 0 ? (
              <>
                <select
                  value={myAccounts.currentAccountId || ''}
                  onChange={handleBusinessSwitch}
                  disabled={switching}
                  style={styles.bizSelect}
                  title="Switch business"
                >
                  {myAccounts.accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.name}</option>
                  ))}
                </select>
                {switching && <span style={styles.switchingText}>Switching…</span>}
              </>
            ) : (
              <span style={{ ...styles.bizSelect, cursor: 'default', color: '#64748b' }}>
                {myAccounts?.accounts?.[0]?.name || 'Loading…'}
              </span>
            )}
          </div>
        )}

        {/* Business Unit Filter */}
        {activeUnits.length > 0 && (
          <div style={styles.switcherWrap}>
            <span style={styles.switcherLabel}>🏗</span>
            <select
              value={selectedBu || ''}
              onChange={e => setSelectedBu(e.target.value ? Number(e.target.value) : null)}
              style={styles.buSelect}
              title="Filter by business unit"
            >
              <option value="">All Units</option>
              {activeUnits.map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Sync ST Data Button */}
        {canRefresh && (
          <button
            onClick={handleSync}
            disabled={syncing}
            style={{ ...styles.refreshBtn, opacity: syncing ? 0.6 : 1, background: syncDone ? '#dcfce7' : '#f1f5f9', color: syncDone ? '#16a34a' : '#475569' }}
            title="Sync jobs and appointments from ServiceTitan"
          >
            <span style={{ display: 'inline-block', animation: syncing ? 'spin 0.8s linear infinite' : 'none' }}>
              🔄
            </span>
            {syncing ? ' Syncing…' : syncDone ? ' Synced!' : ' Sync ST'}
          </button>
        )}

        {/* Refresh Button */}
        {canRefresh && (
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            style={{ ...styles.refreshBtn, opacity: refreshing ? 0.6 : 1 }}
            title="Refresh data"
          >
            <span style={{ display: 'inline-block', animation: refreshing ? 'spin 0.8s linear infinite' : 'none' }}>
              🔄
            </span>
            {refreshing ? ' Refreshing…' : ' Refresh'}
          </button>
        )}
      </div>
    </header>
  );
}

const styles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1.25rem 1.75rem',
    background: '#fff',
    borderBottom: '1px solid #e2e8f0',
    position: 'sticky',
    top: 0,
    zIndex: 10,
    flexWrap: 'wrap',
    gap: '0.75rem',
  },
  title: { margin: 0, fontSize: '1.25rem', fontWeight: '700', color: '#1e293b' },
  switcherWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '0.5rem',
    padding: '0.25rem 0.5rem',
  },
  switcherLabel: { fontSize: '0.875rem', lineHeight: 1 },
  bizSelect: {
    padding: '0.25rem 0.375rem',
    border: 'none',
    borderRadius: '0.375rem',
    fontSize: '0.8rem',
    fontWeight: '700',
    color: '#1e293b',
    background: 'transparent',
    cursor: 'pointer',
    outline: 'none',
    maxWidth: '140px',
  },
  buSelect: {
    padding: '0.25rem 0.375rem',
    border: 'none',
    borderRadius: '0.375rem',
    fontSize: '0.8rem',
    fontWeight: '600',
    color: '#475569',
    background: 'transparent',
    cursor: 'pointer',
    outline: 'none',
    maxWidth: '140px',
  },
  switchingText: { fontSize: '0.7rem', color: '#94a3b8', whiteSpace: 'nowrap' },
  refreshBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.5rem 1rem',
    background: '#f1f5f9',
    border: '1px solid #e2e8f0',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#475569',
    transition: 'all 0.15s',
  },
};
